import React, { ReactNode, useState } from "react";
import {
  TableBody,
  TableBulkItemRow,
  TableBulkItemHeader,
  toggleItemWithSet,
  HEADER_ID,
  itemCheckedWithSet,
  toggleAll,
  TableHeaderItem,
  TableSearchEdit,
  getColumns
} from "collections/Table";
import {
  DeviceSortDirection,
  DevicesCardPropsFragment,
  useDevicesLocationQuery,
  DevicesLocationQuery,
  useRestartDevicesBySiteMutation
} from "generated/graphql";
import { useTranslation } from "utilities/i18n";
import { infiniteNestLink, Link } from "polyrouter";
import { handleNullFields } from "utilities/stringUtils";
import { SortState } from "store/filters";
import { TABLES, TABLE_COLUMNS, TABLE_COLUMN_STATE } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { verifyAndFormatAggregates } from "utilities/filters/deviceFilters";
import { hasValue } from "utilities/filters/filters";
import { generateTableHeaders } from "utilities/tableUtils";
import { Guard, ProtectedLink, ProtectedRoute } from "TsRoleGuards";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import {
  CancelButton,
  FeatureFlagEnabled,
  PremiumBetaBadge,
  SubmitButton
} from "elements";
import { DISMISS, useNotification } from "collections";
import { ExpandCellVisibility } from "routes/Manage/Inventory/List/TableView";
import { NestedLinesTable } from "routes/Manage/Inventory/List/NestedLinesTable";
import { isPolyOS, deviceHasExtraOptions } from "utilities/modelSelectors";
import { formatDate } from "routes/Manage/Inventory/View/DeviceDetails";
import { DESKTOP_PROXY_AGENT } from "Constants";
import {
  getProvisioningState,
  ProvisioningState
} from "elements/ConnectionStatusIndicator/ConnectionStatusIndicator";
import { generatePath } from "react-router";

type DeviceRowProps = DevicesCardPropsFragment & {
  onCheck: (id: string) => void;
  checked: boolean;
  shownColumns: Array<string>;
};

export const DeviceRow: React.FC<DeviceRowProps> = ({
  id,
  name,
  internalIp,
  macAddress,
  hardwareModel,
  softwareVersion,
  site,
  room,
  onCheck,
  checked,
  shownColumns,
  lastConfigRequestDate,
  supportsSettings,
  supportsSoftwareUpdate,
  provisioningEnabled,
  proxyAgent,
  dateRegistered,
  lastDetected
}) => {
  const { t } = useTranslation();
  const COLUMNS = TABLE_COLUMNS.DEVICES;
  const [subTableOpen, setSubTableOpen] = useState(false);
  const isDesktopDevice = proxyAgent === DESKTOP_PROXY_AGENT;
  const deviceHasOptions = deviceHasExtraOptions(
    supportsSettings,
    supportsSoftwareUpdate,
    provisioningEnabled
  );
  const isPolyOs = isPolyOS(hardwareModel ?? "");
  const provisioningState = getProvisioningState(lastConfigRequestDate);

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link to={generatePath("/manage/inventory/details/:id", { id })}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.IPV4:
        content = handleNullFields(internalIp, true);
        break;
      case COLUMNS.MAC_ADDRESS:
        content = handleNullFields(macAddress, true);
        break;
      case COLUMNS.SITE:
        content = handleNullFields(site?.name, true);
        break;
      case COLUMNS.ROOM:
        content = handleNullFields(room?.name, true);
        break;
      case COLUMNS.MODEL:
        content = handleNullFields(hardwareModel, true);
        break;
      case COLUMNS.VERSION:
        content = handleNullFields(softwareVersion, true);
        break;
      case COLUMNS.LINES:
        content = <ExpandCellVisibility id={id} onChange={setSubTableOpen} />;
        break;
      case COLUMNS.LAST_PROVISIONING_SYNC:
        content =
          deviceHasOptions && !isDesktopDevice
            ? provisioningState === ProvisioningState.ACTIVE ||
              provisioningState === ProvisioningState.INACTIVE
              ? formatDate(lastConfigRequestDate)
              : handleNullFields(null, true)
            : "-";
        break;
      case COLUMNS.DATE_REGISTERED:
        content =
          deviceHasOptions && !isDesktopDevice
            ? dateRegistered
              ? formatDate(dateRegistered)
              : t("REGISTER_DEVICE_PRIOR")
            : "-";
        break;
      case COLUMNS.DATE_LAST_SEEN:
        content =
          deviceHasOptions && !isDesktopDevice && lastDetected
            ? formatDate(lastDetected, "P")
            : handleNullFields(null, true);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <>
      <TableBulkItemRow
        checked={checked}
        showChecks={false}
        onCheck={() => onCheck(id)}
      >
        {shownColumns.map(getTableContent)}
      </TableBulkItemRow>
      {subTableOpen && <NestedLinesTable deviceId={id} isPolyOs={isPolyOs} />}
    </>
  );
};

export const DeviceTable: React.FC<{
  siteId?: string;
  roomId?: string;
}> = ({ siteId, roomId }) => {
  const { COLUMNS, HIDDEN } = TABLES.DEVICES;
  const { t } = useTranslation();
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const [sort, setSort] = useState({
    field: "name",
    direction: "ASC"
  } as SortState);
  const [searchTerm, setSearch] = useState<undefined | string>(undefined);
  const defaultFilters = {
    [COLUMNS.MODEL]: "",
    [COLUMNS.SITE]: "",
    [COLUMNS.ROOM]: "",
    [COLUMNS.VERSION]: ""
  };
  const [filters, setFilters] = useState<Record<string, string>>(
    defaultFilters
  );
  const [restarted, setRestarted] = useState(false);
  const [, restart] = useRestartDevicesBySiteMutation();

  const showSite = !!roomId;
  const { NAME: TABLE_NAME } = showSite
    ? TABLES.ROOM_DEVICES
    : TABLES.SITE_DEVICES;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  let filterById = {};
  if (siteId) {
    filterById = {
      field: "siteUUID",
      eq: siteId
    };
  } else if (roomId) {
    filterById = {
      field: "roomUUID",
      eq: roomId
    };
  }

  const { data, fetching, stale } = useDevicesLocationQuery({
    variables: {
      from: 0,
      boundsFilter: {
        AND: [
          { ...filterById },
          ...Object.entries(filters).map(([key, value]) => ({
            field: key,
            contains: value
          })),
          { searchTerm }
        ]
      },
      sort: {
        fields: [
          {
            name: sort.field,
            direction: sort.direction as DeviceSortDirection
          }
        ]
      },
      aggregations: [
        {
          term: "hardwareModel"
        },
        {
          term: "softwareVersion"
        },
        {
          term: "site"
        },
        {
          term: "room"
        }
      ]
    }
  });

  const loading = fetching || stale;
  const devices = data?.deviceList ?? [];
  const aggregates: DevicesLocationQuery["deviceAggregations"] =
    data?.deviceAggregations ?? [];
  const verifyAndFormat = verifyAndFormatAggregates(aggregates);
  const getValue = (bucket: { value: string; count: number }) => bucket.value;
  const filterOptions = {
    [COLUMNS.MODEL]: verifyAndFormat("hardwareModel").map(getValue),
    [COLUMNS.SITE]: verifyAndFormat("site").map(getValue),
    [COLUMNS.ROOM]: verifyAndFormat("room").map(getValue),
    [COLUMNS.VERSION]: verifyAndFormat("softwareVersion").map(getValue)
  };

  const updateFilters = (field: string, value: string) => {
    setFilters({
      ...filters,
      ...{ [field]: value }
    });
  };

  const hasActiveFilter = Object.keys(filters).some(filter =>
    hasValue(filters, filter)
  );

  const toggleItem = toggleItemWithSet(
    devices.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const columnTranslations = {
    [COLUMNS.NAME]: t("DEVICE_NAME"),
    [COLUMNS.IPV4]: t("IPV4"),
    [COLUMNS.MAC_ADDRESS]: t("MAC_ADDRESS"),
    [COLUMNS.SITE]: t("SITE"),
    [COLUMNS.ROOM]: t("ROOM"),
    [COLUMNS.MODEL]: t("DEVICE_MODEL"),
    [COLUMNS.VERSION]: t("VERSION"),
    [COLUMNS.LINES]: t("LINES"),
    [HIDDEN.DATE_LAST_SEEN]: t("DEVICE_LAST_SEEN"),
    [HIDDEN.LAST_PROVISIONING_SYNC]: t("LAST_PROVISIONING_TOUCH"),
    [HIDDEN.DATE_REGISTERED]: t("DEVICE_ADDED")
  };

  const getTableHeaders = (column: string, title: string) => {
    const filterValue = filters[column];
    const filterable = filterValue !== undefined;
    const filter = filterable
      ? {
          filterValue,
          filterOptions: filterOptions[column],
          onFilter: updateFilters
        }
      : undefined;

    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        sort={{
          currentSort: sort,
          onSort: setSort
        }}
        filter={filter}
        key={column}
      />
    );
  };

  const columnFilterFn = (column: string) =>
    showSite ? column !== COLUMNS.ROOM : column !== COLUMNS.SITE;
  const shown = getColumns(shownColumns, columnTranslations, columnFilterFn);
  const hidden = getColumns(hiddenColumns, columnTranslations, columnFilterFn);

  const restartDevices = async () => {
    if (!siteId) {
      return;
    }
    const { data } = await restart({ siteId });
    return data?.rebootDevicesBySiteId;
  };

  return (
    <>
      <div className="table-actions mb-1">
        {siteId && (
          <FeatureFlagEnabled feature="SITE_REBOOT">
            <ProtectedLink
              to={!restarted ? infiniteNestLink("/restart") : ""}
              className={`btn btn-primary ${restarted ? "disabled" : ""}`}
              requirement="update:device"
            >
              {t("RESTART_DEVICES")}
            </ProtectedLink>
            <Guard requirement="update:device">
              <PremiumBetaBadge />
            </Guard>
          </FeatureFlagEnabled>
        )}
        <TableSearchEdit
          search={{
            onSearch: setSearch
          }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </div>
      <div className="table-container">
        <table className="table custom-table table-filterable">
          <TableBulkItemHeader
            checked={itemChecked(HEADER_ID)}
            onCheck={() =>
              toggleAll(
                devices.map(device => device.id),
                selectedItems,
                setSelectedItems
              )
            }
            showChecks={false}
            onClearFilters={() => setFilters(defaultFilters)}
            showClearFilters={hasActiveFilter}
          >
            {generateTableHeaders(shown, getTableHeaders)}
          </TableBulkItemHeader>
          <TableBody loading={loading} cols={shown.length} accommodateScrollbar>
            {devices.map(device => (
              <DeviceRow
                {...device}
                key={device.id}
                onCheck={() => toggleItem(device.id)}
                checked={itemChecked(device.id)}
                shownColumns={shown.map(column => column.key)}
              />
            ))}
          </TableBody>
        </table>
      </div>
      <FeatureFlagEnabled feature="SITE_REBOOT">
        <ProtectedRoute
          nest
          path="restart"
          requirement="update:device"
          render={() =>
            siteId && (
              <RestartDevicesModal
                restart={restartDevices}
                onSuccess={() => setRestarted(true)}
              />
            )
          }
        />
      </FeatureFlagEnabled>
    </>
  );
};

export const RestartDevicesModal: React.FC<{
  restart: () => Promise<
    undefined | { success: boolean; error: string | null }
  >;
  onSuccess: () => void;
}> = ({ restart, onSuccess }) => {
  const { t } = useTranslation();
  const dispatchEvent = useModalDispatch();
  const [addNotification] = useNotification();

  const restartDevices = async () => {
    const response = await restart();

    if (response?.success) {
      addNotification({
        type: "success",
        message: t("SUCCESSFULLY_RESTARTED_DEVICES"),
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
      onSuccess();
    } else {
      addNotification({
        type: "error",
        message: t("ERROR_RESTART_DEVICES", { errorMessage: response?.error }),
        persistThroughNav: true,
        dismiss: DISMISS.USER_ONLY
      });
    }
    dispatchEvent();
  };

  return (
    <DefaultModalForm
      title={t("RESTART_DEVICES")}
      onSubmit={restartDevices}
      name="RestartDevice"
    >
      {({ isSubmitting }) => (
        <>
          <p>{t("RESTART_DEVICES_CONFIRMATION")}</p>
          <p>{t("PREMIUM_BETA_FEATURE")}</p>
          <div className="btn-container">
            <CancelButton onClick={dispatchEvent} />
            <SubmitButton
              isSubmitting={isSubmitting}
              testId="restart"
              labelKey="RESTART"
              submittingLabelKey="RESTARTING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};
